/* The glass steps `tokens/surface.css` publishes, and the one normalizer every
 * consumer of them needs.
 *
 * ⚠ THIS FILE IS THE SINGLE DEFINITION — components re-export from here rather
 * than declaring the list again. `surface.css` is the authority the names are
 * checked against: `tokens/token-integrity.spec.ts` asserts every name here has
 * a matching `[data-dds-surface="…"]` selector there, and vice versa.
 */

/** The glass steps surface.css publishes. */
export const SURFACE_PRESETS = [
  "glass-none",
  "glass-element",
  "glass-2",
  "glass-3",
  "glass-surface",
  "opaque"
] as const;

export type SurfacePreset = (typeof SURFACE_PRESETS)[number];

/**
 * Normalize a caller-supplied surface name to "a name" or "omitted".
 *
 * ⚠ BLANK IS NOT THE SAME AS OMITTED TO REACT. `data-dds-surface={""}` renders
 * a PRESENT attribute, and `surface.css`'s nested-surface reset keys off
 * ABSENCE — so a blank string is excluded from the reset, matches no preset
 * selector, and inherits whatever an ANCESTOR resolved `--surface-fill` and
 * `--surface-backdrop` to. `surface` is CMS-fed and an unset select round-trips
 * as `""` or a lone space, so blank is ordinary input.
 *
 * Feed the result to BOTH the dev warning and the JSX: an un-normalized warning
 * reports `unknown surface ""` where the honest reading is "omitted".
 */
export function resolveSurface(
  surface: string | undefined
): string | undefined {
  const trimmed = surface?.trim();
  return trimmed ? trimmed : undefined;
}

/** Whether a NORMALIZED surface name matches a preset surface.css publishes. */
export function isSurfacePreset(surface: string): surface is SurfacePreset {
  return (SURFACE_PRESETS as readonly string[]).includes(surface);
}

/**
 * The value a component may RENDER as `data-dds-surface`: the normalized name
 * when it is a published preset, otherwise nothing.
 *
 * ⚠ An unknown NON-blank name rendered as an attribute lands in the same state
 * a blank one does — excluded from surface.css's absence-keyed reset, matching
 * no preset selector, inheriting a preset-carrying ancestor's values. Gating on
 * the published list means a typo renders NO attribute, so the reset applies,
 * both var() fallbacks fire, and the element keeps its own Figma surface.
 *
 * Feed the WARNING from resolveSurface(), not from here — it must name the
 * unknown value this function just dropped.
 */
export function resolveSurfaceAttribute(
  surface: string | undefined
): SurfacePreset | undefined {
  const normalized = resolveSurface(surface);
  return normalized !== undefined && isSurfacePreset(normalized) ?
    normalized :
    undefined;
}
